import React, { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet';
import { FaHeartbeat, FaChevronDown, FaChevronUp, FaArrowLeft } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import { goReserve, goBackSmart } from '../../utils/navigation';
import { useTranslation } from 'react-i18next';

interface FaqMedicalDietMaleProps {
  ipAllowed: boolean;
}

type QA = { q: string; a: string | string[] };
type AnyObj = Record<string, any>;

async function loadFromPublic(lang: 'ja' | 'zh'): Promise<AnyObj | null> {
  try {
    const res = await fetch(`/data/faq_${lang}.json`, { cache: 'no-store' });
    if (!res.ok) return null;
    return (await res.json()) as AnyObj;
  } catch {
    return null;
  }
}
function pick<T>(root: any, path: string, fallback: T): T {
  if (!root) return fallback;
  const segs = path.split('.');
  let cur: any = root;
  for (const s of segs) {
    if (cur && typeof cur === 'object' && s in cur) cur = cur[s];
    else return fallback;
  }
  return cur as T;
}

const OVERVIEW = {
  title: 'メディカルダイエット（男性）について',
  paras: [
    '当院のメディカルダイエットは、医師の診療のもとで科学的根拠にもとづいた医薬品を用い、食欲の調整や代謝改善を図るプログラムです。オンライン診療により生活に合わせた継続を支援します。',
    '主な選択肢は、GLP-1受容体作動薬（経口：リベルサス®、注射：マンジャロ®）と、代謝・排糖を助ける薬剤（SGLT2阻害薬：フォシーガ®／ビグアナイド：メトホルミン）です。体質・既往歴・目標に応じて個別に設計します。',
  ],
};

const AGENTS = {
  title: '主なお薬と作用の違い',
  glp1_oral: {
    k: 'リベルサス®（経口GLP-1RA）',
    v: [
      '食欲中枢に作用し、空腹感を抑える／食後血糖の上昇を緩やかにする。',
      '毎日内服。用量は段階的に増量して効果と副作用のバランスを最適化。',
    ],
  },
  glp1_inj: {
    k: 'マンジャロ®（注射GLP-1/GIP作動薬）',
    v: [
      '週1回自己注射。食欲抑制に加えてエネルギー消費（熱産生）・脂肪分解の促進が期待できる。',
      '当院ではオンラインで自己注射指導を実施。',
    ],
  },
  sglt2: {
    k: 'フォシーガ®（SGLT2阻害薬）',
    v: [
      '腎臓での再吸収を抑え、尿に糖を排泄（1錠で約200〜500kcal相当の糖排出が報告）。',
      '体内エネルギーバランスをマイナスにし、減量を後押し。',
    ],
  },
  met: {
    k: 'メトホルミン（ビグアナイド）',
    v: [
      'インスリン抵抗性の改善、食欲抑制・脂肪蓄積抑制などを通じて体重減少をサポート。',
      '糖代謝の土台を整え、他薬の効果を引き出す目的でも用いられる。',
    ],
  },
};

const SUITABILITY = {
  title: '適応・注意事項（男性）',
  ok: [
    'BMIが高い／近年の体重増加が続いている',
    '食欲コントロールが難しい、間食が多い、夜に過食しがち',
    '内臓脂肪が多く健康診断で指摘がある（脂質異常・高血圧・耐糖能異常など）',
  ],
  caution: [
    '重度の胃腸障害、膵炎既往、重い腎機能障害、脱水傾向が強い場合（SGLT2は特に注意）',
    '長期大量飲酒や高度の肝機能障害（メトホルミンは乳酸アシドーシスに注意）',
    '他疾患の治療薬との相互作用（必ず服用中のお薬を申告してください）',
  ],
};

const SIDE_EFFECTS = {
  title: '副作用のめやす',
  items: [
    {
      k: 'GLP-1系（リベルサス®／マンジャロ®）',
      v: [
        '悪心・胃部不快・嘔気、食後の膨満感（開始〜増量初期に出やすい／多くは時間とともに軽快）',
        'まれに膵炎など重篤例の報告。持続する強い腹痛・嘔吐は受診を。',
      ],
    },
    {
      k: 'SGLT2阻害薬（フォシーガ®）',
      v: [
        '頻尿・喉の渇き、体液量低下、尿路・性器感染症の増加（こまめな水分補給と清潔ケア）',
        'まれにケトアシドーシス。著しい倦怠感・腹痛・呼吸が浅い等あれば受診。',
      ],
    },
    {
      k: 'メトホルミン',
      v: [
        '胃部不快・下痢・金属味（少量開始・食後内服で軽減）',
        '極めてまれに乳酸アシドーシス。強い倦怠感・呼吸促迫・筋痛は至急受診。',
      ],
    },
  ],
  note: '※ 副作用には個人差があります。異常を感じた場合は自己判断で継続せず、医師にご相談ください。',
};

const EXPECTATION = {
  title: '効果の見え方・タイムライン',
  points: [
    '1〜2週：食欲の変化／満腹感の早まりを感じ始める方がいます（GLP-1系）。',
    '4〜8週：体重・ウエストの緩やかな低下。行動習慣が整うほど効果が安定。',
    '12週以降：用量調整と併用戦略で、停滞期を乗り越えながら減量幅を積み上げる。',
  ],
  foot: '体重は「薬理＋生活習慣」の掛け算です。睡眠・タンパク質・活動量を整えると減量効率が向上します。',
};

const LIFESTYLE = {
  title: '併用すると良い生活アプローチ',
  items: [
    'タンパク質を毎食で確保（体重×1.0〜1.5g/日を目安に個別調整）',
    '食物繊維・発酵食品で腸内環境を整える',
    '夜遅い時間の糖質・アルコールを控える',
    'レジスタンス運動＋日常の歩行を増やす（NEATの底上げ）',
    '7時間前後の睡眠を安定化（食欲ホルモンの乱れを防ぐ）',
  ],
};

const QA_FALLBACK: Record<'ja' | 'zh', QA[]> = {
  ja: [
    {
      q: 'どのお薬から始めれば良いですか？',
      a: [
        '食欲の強さ・BMI・既往症・仕事や生活パターンを踏まえ、GLP-1系（経口/注射）・SGLT2・メトホルミンの中から医師が提案します。',
        'まずは単剤で様子見 → 反応に応じて用量調整や併用を検討する流れが一般的です。',
      ],
    },
    {
      q: 'どれくらい体重が落ちますか？',
      a: [
        '個人差が大きいですが、生活改善と併用すると数か月スパンで有意な減量が期待できます。',
        '目標は「短期の最大減量」より「無理なく続く再現性」。停滞期は用量・食事・活動の見直しで突破します。',
      ],
    },
    {
      q: '停滞期が来たらどうすれば？',
      a: [
        '摂取カロリーの再評価（無意識の間食・飲料を点検）とタンパク質量のテコ入れ。',
        '活動量（歩数・立位時間・階段利用）を底上げし、睡眠の质を確保。',
        '薬剤は増量・併用切り替えの余地を医師と相談します。',
      ],
    },
    {
      q: '副作用が心配です。安全性は？',
      a: [
        '初期は胃腸症状などが出やすい薬剤がありますが、多くは漸増と生活工夫で軽快します。',
        '重い症状（激しい腹痛・持続嘔吐・著しい倦怠感など）は中止して受診してください。',
      ],
    },
    {
      q: 'お酒やトレーニングとの相性は？',
      a: [
        '多量飲酒は減量を妨げ、副作用リスクも高めます。量・頻度を最小限に。',
        '筋トレとの相性は良好。GLP-1で食事量が減るため、タンパク質と電解質補給を意識しましょう。',
      ],
    },
    {
      q: 'いつやめてもリバウンドしませんか？',
      a: [
        '薬は「行動変容をブースト」するための道具です。習慣が固定化されるほどリバウンドしにくくなります。',
        '中止時は段階的に。体重・行動指標（歩数/睡眠/食事）を見ながら医師と計画します。',
      ],
    },
  ],
  zh: [
    {
      q: '应该从哪种药物开始？',
      a: [
        '会综合食欲强度、BMI、既往病史与作息模式，在 GLP-1 系 (口服/注射)、SGLT2 抑制剂、二甲双胍中提出建议；',
        '通常先单药观察，再按反应调整剂量或考虑联用。',
      ],
    },
    {
      q: '一般能减多少？',
      a: [
        '个体差较大；若与生活方式干预并行，通常以“数月”为单位可见明显下降；',
        '目标重在“可持续与可复现”，平台期通过剂量/饮食/活动量的微调来突破。',
      ],
    },
    {
      q: '遇到平台期怎么办？',
      a: [
        '复盘能量摄入（检查无意识零食/饮料），提升蛋白质；',
        '抬升活动量（步数/站立/楼梯）并保证睡眠质量；',
        '与医师评估加量或联用的空间。',
      ],
    },
    {
      q: '担心副作用与安全性。',
      a: [
        '起始阶段部分药物以胃肠道不适为主，通常可通过“缓慢加量 + 生活小技巧”改善；',
        '若出现严重症状（剧烈腹痛/持续呕吐/明显乏力等）请立即停药并就医。',
      ],
    },
    {
      q: '和饮酒、健身的关系？',
      a: [
        '大量饮酒会妨碍减重并增加不良反应风险，建议尽量少量低频；',
        '与力量训练相容性好；使用 GLP-1 时注意蛋白质与电解质补给。',
      ],
    },
    {
      q: '停药会不会反弹？',
      a: [
        '药物是“行为改变加速器”。习惯固定化越彻底，反弹越不易；',
        '建议分阶段停药，并结合体重与行为指标制定计划。',
      ],
    },
  ],
};

const LOCAL_ZH = {
  page: {
    title: '男性医学减重 FAQ',
    back: '返回',
    cta_menu: '前往男性减重菜单',
    cta_reserve: '在线预约',
    disclaimer: '※ 本页面仅提供一般性信息，诊断与治疗的最终判断请务必依据医师面诊。',
  },
  overview: {
    title: '关于男性医学减重',
    paras: [
      '本院的医学减重在医师诊疗下，采用循证药物以调节食欲与改善代谢，并通过在线诊疗支持您在生活节奏中长期坚持。',
      '主要选项包括 GLP-1 受体激动剂 (口服：口服司美格鲁肽 (Rybelsus)；注射：替尔泊肽注射液 (Mounjaro)) 与帮助代谢/排糖的药物 (SGLT2 抑制剂：达格列净片 (Forxiga)；二甲双胍 (Metformin))。会依据体质、既往病史与目标进行个体化制定。',
    ],
  },
  agents: {
    title: '主要药物与作用差异',
    glp1_oral: {
      k: '口服司美格鲁肽 (Rybelsus)',
      v: [
        '作用于食欲中枢，抑制饥饿感/平缓餐后血糖上升；',
        '每日口服；按阶段递增剂量以平衡疗效与不适。',
      ],
    },
    glp1_inj: {
      k: '替尔泊肽注射液 (Mounjaro)',
      v: [
        '每周 1 次自我注射；除抑制食欲外，亦有望促进能量消耗与脂肪分解；',
        '提供在线自我注射操作指导。',
      ],
    },
    sglt2: {
      k: '达格列净片 (Forxiga)',
      v: [
        '抑制肾脏对葡萄糖的重吸收，使糖随尿排出（每片约 200–500 kcal 等量排糖）。',
        '制造能量赤字，从而推动减重。',
      ],
    },
    met: {
      k: '二甲双胍 (Metformin)',
      v: [
        '改善胰岛素抵抗，抑制食欲与脂肪堆积，辅助体重下降；',
        '用于“打牢糖代谢地基”，也可提高其他药物的效果。',
      ],
    },
  },
  side_effects: {
    title: '常见不良反应提示',
    items: [
      {
        k: 'GLP-1 系 (口服司美格鲁肽 (Rybelsus) / 替尔泊肽注射液 (Mounjaro))',
        v: [
          '恶心、胃部不适、呕吐、餐后胀满（多见于起始/加量初期；多数可随时间缓解）；',
          '罕见胰腺炎。如出现持续性剧烈腹痛/呕吐，请及时就医。',
        ],
      },
      {
        k: 'SGLT2 抑制剂 (达格列净片 (Forxiga))',
        v: [
          '尿频、口渴、体液量下降；尿路/生殖道感染风险上升（注意补水与清洁护理）；',
          '罕见酮症酸中毒。若明显乏力/腹痛/呼吸浅快，请及时就医。',
        ],
      },
      {
        k: '二甲双胍 (Metformin)',
        v: [
          '胃部不适、腹泻、金属味（小剂量起始/餐后服用可减轻）；',
          '极罕见乳酸性酸中毒。若出现明显乏力、呼吸促迫、肌痛等，请立即就医。',
        ],
      },
    ],
    note: '※ 不良反应存在个体差异。如有异常切勿自行坚持用药，请与医师联系。',
  },
};

const FaqMedicalDietMale: React.FC<FaqMedicalDietMaleProps> = ({ ipAllowed }) => {
  const navigate = useNavigate();
  const { i18n } = useTranslation();
  const lang: 'ja' | 'zh' = i18n.language === 'zh' ? 'zh' : 'ja';

  const [root, setRoot] = useState<AnyObj | null>(null);
  useEffect(() => {
    let mounted = true;
    (async () => {
      const remote = await loadFromPublic(lang);
      if (!mounted) return;
      setRoot(remote);
    })();
    return () => {
      mounted = false;
    };
  }, [lang]);

  const J = pick<any>(root, 'faq.medical_diet_male', null);

  const page = pick(J, 'page', lang === 'zh' ? LOCAL_ZH.page : {
    title: 'メディカルダイエット（男性）FAQ',
    back: '戻る',
    cta_menu: 'メニュー（男性ダイエット）へ',
    cta_reserve: 'オンラインで予約する',
    disclaimer:
      '※ 本ページは一般的な情報の提供を目的としています。診断や治療の最終判断は必ず医師の診察に基づき行ってください。',
  });

  const OVERVIEW_DATA = pick(J, 'overview', lang === 'zh' ? LOCAL_ZH.overview : OVERVIEW);
  const AGENTS_DATA = pick(J, 'agents', lang === 'zh' ? LOCAL_ZH.agents : AGENTS);
  const SUITABILITY_DATA = pick(J, 'suitability', SUITABILITY);
  const SIDE_EFFECTS_DATA = pick(J, 'side_effects', lang === 'zh' ? LOCAL_ZH.side_effects : SIDE_EFFECTS);
  const EXPECTATION_DATA = pick(J, 'expectation', EXPECTATION);
  const LIFESTYLE_DATA = pick(J, 'lifestyle', LIFESTYLE);
  const QA_LIST: QA[] = pick(J, 'qa', QA_FALLBACK[lang]);

  const [open, setOpen] = useState<number | null>(0);
  const toggle = (idx: number) => setOpen((cur) => (cur === idx ? null : idx));

  const jsonLd = useMemo(
    () => ({
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: QA_LIST.slice(0, 25).map((qa) => ({
        '@type': 'Question',
        name: qa.q,
        acceptedAnswer: { '@type': 'Answer', text: Array.isArray(qa.a) ? qa.a.join('\n') : qa.a },
      })),
    }),
    [QA_LIST]
  );

  return (
    <div className="min-h-screen pt-28 pb-20 bg-sky-50/70 font-sans" translate="no">
      <Helmet>
        <title>{page.title} - Melodis Clinic</title>
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <div className="container mx-auto px-4 max-w-5xl">
        <div className="bg-white border border-slate-100 rounded-3xl shadow-sm p-6 md:p-9">
          {/* Back */}
          <div className="mb-3">
            <button
              type="button" onClick={() => goBackSmart(navigate, '/faq')}
              className="inline-flex items-center gap-2 text-slate-600 hover:text-slate-800 text-sm"
            >
              <FaArrowLeft className="w-4 h-4" />
              {page.back}
            </button>
          </div>

          {/* Header */}
          <div className="flex items-center mb-6">
            <div className="mr-4 rounded-2xl bg-sky-100 p-3">
              <FaHeartbeat className="text-sky-600 w-7 h-7" />
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-slate-900">
              <span className="text-sky-400 mr-2">│</span>{page.title}
            </h1>
          </div>

          {/* Overview */}
          <section className="rounded-2xl border border-sky-100 bg-sky-50 p-6 md:p-7 mb-8">
            <h2 className="text-2xl font-semibold text-sky-700 mb-3">{OVERVIEW_DATA.title}</h2>
            <div className="space-y-3 text-slate-800 leading-relaxed">
              {(OVERVIEW_DATA.paras || []).map((p: string, i: number) => <p key={i}>{p}</p>)}
            </div>
          </section>

          {/* Agents */}
          <section className="rounded-2xl border border-slate-200 bg-white p-6 md:p-7 mb-6">
            <h3 className="text-xl font-semibold text-slate-900 mb-3">{AGENTS_DATA.title}</h3>
            <div className="grid md:grid-cols-2 gap-4">
              {[AGENTS_DATA.glp1_oral, AGENTS_DATA.glp1_inj, AGENTS_DATA.sglt2, AGENTS_DATA.met]
                .filter(Boolean)
                .map((card: any, idx: number) => (
                  <div key={idx} className="rounded-xl border border-slate-200 bg-slate-50 p-5">
                    <div className="font-semibold text-slate-900 mb-1">{card.k}</div>
                    <ul className="list-disc pl-5 space-y-1 text-slate-800 text-sm">
                      {(card.v || []).map((line: string, i: number) => <li key={i}>{line}</li>)}
                    </ul>
                  </div>
                ))}
            </div>
          </section>

          {/* Suitability / Caution */}
          <section className="rounded-2xl border border-slate-200 bg-white p-6 md:p-7 mb-6">
            <h3 className="text-xl font-semibold text-slate-900 mb-3">適応と注意事項</h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-5">
                <div className="font-semibold text-slate-900 mb-1">こんな方に向いています</div>
                <ul className="list-disc pl-5 space-y-1 text-slate-800 text-sm">
                  {(SUITABILITY_DATA.ok || []).map((x: string, i: number) => <li key={i}>{x}</li>)}
                </ul>
              </div>
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-5">
                <div className="font-semibold text-slate-900 mb-1">注意が必要なケース</div>
                <ul className="list-disc pl-5 space-y-1 text-slate-800 text-sm">
                  {(SUITABILITY_DATA.caution || []).map((x: string, i: number) => <li key={i}>{x}</li>)}
                </ul>
                <p className="text-slate-600 text-xs mt-2">
                  ※ 医師が個別の禁忌・併用注意を確認します。必ず既往歴・内服中の薬を申告してください。
                </p>
              </div>
            </div>
          </section>

          {/* Side effects */}
          <section className="rounded-2xl border border-slate-200 bg-white p-6 md:p-7 mb-6">
            <h3 className="text-xl font-semibold text-slate-900 mb-3">{SIDE_EFFECTS_DATA.title}</h3>
            <div className="grid md:grid-cols-3 gap-4">
              {(SIDE_EFFECTS_DATA.items || []).map((card: any, idx: number) => (
                <div key={idx} className="rounded-xl border border-slate-200 bg-slate-50 p-5">
                  <div className="font-semibold text-slate-900 mb-1">{card.k}</div>
                  <ul className="list-disc pl-5 space-y-1 text-slate-800 text-sm">
                    {(card.v || []).map((line: string, i: number) => <li key={i}>{line}</li>)}
                  </ul>
                </div>
              ))}
            </div>
            {SIDE_EFFECTS_DATA.note && <p className="text-slate-600 text-xs mt-3">{SIDE_EFFECTS_DATA.note}</p>}
          </section>

          {/* Expectation & Lifestyle */}
          <section className="rounded-2xl border border-slate-200 bg-white p-6 md:p-7 mb-6">
            <h3 className="text-xl font-semibold text-slate-900 mb-3">
              {lang === 'zh' ? '疗效呈现与时间轴' : '経過の目安とコツ'}
            </h3>
            <div className="grid md:grid-cols-2 gap-4">
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-5">
                <div className="font-semibold text-slate-900 mb-1">{EXPECTATION_DATA.title}</div>
                <ul className="list-disc pl-5 space-y-1 text-slate-800 text-sm">
                  {(EXPECTATION_DATA.points || []).map((x: string, i: number) => <li key={i}>{x}</li>)}
                </ul>
                {EXPECTATION_DATA.foot && <p className="text-slate-600 text-xs mt-2">{EXPECTATION_DATA.foot}</p>}
              </div>
              <div className="rounded-xl border border-slate-200 bg-slate-50 p-5">
                <div className="font-semibold text-slate-900 mb-1">{LIFESTYLE_DATA.title}</div>
                <ul className="list-disc pl-5 space-y-1 text-slate-800 text-sm">
                  {(LIFESTYLE_DATA.items || []).map((x: string, i: number) => <li key={i}>{x}</li>)}
                </ul>
              </div>
            </div>
          </section>

          {/* Q&A */}
          <section className="rounded-2xl border border-slate-200 bg-white p-4 md:p-5">
            <h3 className="text-xl font-semibold text-slate-900 mb-3">
              {lang === 'zh' ? '常见问题' : 'よくある質問'}
            </h3>
            <div className="space-y-3">
              {QA_LIST.map((qa, idx) => {
                const isOpen = open === idx;
                return (
                  <div key={idx} className="border border-slate-200 rounded-xl bg-slate-50">
                    <button
                      type="button"
                      aria-expanded={isOpen}
                      onClick={() => toggle(idx)}
                      className="w-full flex items-start justify-between gap-4 px-4 py-3 text-left"
                    >
                      <span className="font-medium text-slate-900">{qa.q}</span>
                      {isOpen ? <FaChevronUp className="mt-1 shrink-0 text-slate-400" /> : <FaChevronDown className="mt-1 shrink-0 text-slate-400" />}
                    </button>
                    <div className={`px-4 pb-4 ${isOpen ? '' : 'hidden'}`}>
                      {Array.isArray(qa.a) ? (
                        <ul className="list-disc pl-5 space-y-1 text-slate-800">
                          {(qa.a as string[]).map((line, i) => <li key={i}>{line}</li>)}
                        </ul>
                      ) : (
                        <p className="text-slate-800">{qa.a as string}</p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            <p className="text-slate-500 text-xs mt-4">{page.disclaimer}</p>
          </section>

          {/* CTA */}
          <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
            <button
              onClick={() => navigate('/menu/obesity-male')}
              className="inline-flex items-center gap-2 rounded-full border border-slate-300 px-6 py-3 text-slate-700 hover:bg-slate-50"
            >
              {page.cta_menu}
            </button>
            <button
              onClick={() => goReserve(navigate, ipAllowed)}
              className="inline-block bg-sky-500 hover:bg-sky-600 active:bg-sky-700 text-white px-8 py-3 rounded-full font-semibold shadow-sm transition"
            >
              {page.cta_reserve}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default FaqMedicalDietMale;
